import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import { ArrowBack, Close } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { ref } from "firebase/storage";
import { getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../..";
import {
    deaneryList,
    parishListMap,
    type DeaneryOption as Deanery,
    type ParishOption as Parish,
} from "../../../utils/deanery_parishes";
import { useFeedback } from "../../../providers/feedback/feedback";
import AdminDashboardLayout from "../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import { buildNewsSlug } from "../../../utils/news";
import "react-quill/dist/quill.snow.css";

interface NewsData {
    title: string;
    subtitle: string;
    description: string;
    thumbnail: string;
    postedBy: string;
    gallery?: string[];
    deanerName?: { id: Number; name: string } | null;
    selectedParishName?: { id: Number; value: string } | null;
}

function EditNewsPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showSnackbar } = useFeedback();

    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [postedBy, setPostedBy] = useState("");
    const [subtitle, setSubTitle] = useState("");
    const [description, setDescription] = useState("");
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [existingGallery, setExistingGallery] = useState<string[]>([]);
    const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
    const [existingThumbnail, setExistingThumbnail] = useState<string>("");
    const [parishList, setParishList] = useState<Parish[]>([]);
    const [_selectedDeanery, _setSelectedDeanery] = useState<{
        id: Number;
        name: string;
    } | null>(null);
    const [_selectedParish, _setSelectedParish] = useState<{
        id: Number;
        value: string;
    } | null>(null);

    useEffect(() => {
        if (id) {
            fetchNewsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchNewsData = async () => {
        try {
            setLoading(true);
            console.log("Fetching news with ID:", id);

            if (!id) {
                showSnackbar("No news ID provided");
                navigate("/admin/news");
                return;
            }

            const newsDoc = await getDoc(doc(firestore, "news", id));
            console.log("News document exists:", newsDoc.exists());

            if (newsDoc.exists()) {
                const data = newsDoc.data() as NewsData;
                console.log("News data:", data);
                setTitle(data.title || "");
                setPostedBy(data.postedBy || "");
                setSubTitle(data.subtitle || "");
                setDescription(data.description || "");
                setExistingThumbnail(data.thumbnail || "");
                setExistingGallery(data.gallery || []);

                if (data.deanerName) {
                    _setSelectedDeanery(data.deanerName);
                    const deanery = deaneryList.find(d => d.id === data.deanerName?.id);
                    if (deanery) {
                        setParishList(parishListMap[deanery.name] || []);
                    }
                }

                if (data.selectedParishName) {
                    _setSelectedParish(data.selectedParishName);
                }
            } else {
                console.error("News document does not exist for ID:", id);
                showSnackbar("News not found");
                navigate("/admin/news");
            }
        } catch (error) {
            console.error("Error fetching news:", error);
            showSnackbar("Error loading news data");
            navigate("/admin/news");
        } finally {
            setLoading(false);
        }
    };

    const handleDeaneryChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: Deanery | null
    ) => {
        if (value) {
            _setSelectedDeanery({ id: value.id, name: value.name });
            setParishList(parishListMap[value.name] || []);
            _setSelectedParish(null);
        } else {
            _setSelectedDeanery(null);
            setParishList([]);
        }
    };

    const removeExistingGalleryImage = (imageUrl: string) => {
        setExistingGallery(existingGallery.filter(img => img !== imageUrl));
    };

    const handleUpdate = async () => {
        if (!title || !postedBy || !subtitle || !description) {
            showSnackbar("Please fill all required fields");
            return;
        }

        if (!existingThumbnail && !selectedThumbnail) {
            showSnackbar("Please select a thumbnail");
            return;
        }

        try {
            let thumbnailUrl = existingThumbnail;
            let galleryUrls = [...existingGallery];

            // Upload new thumbnail if selected
            if (selectedThumbnail) {
                const storageRef = ref(storage, `news/${selectedThumbnail.name}`);
                const uploadTask = uploadBytesResumable(storageRef, selectedThumbnail);
                await uploadTask;
                thumbnailUrl = await getDownloadURL(storageRef);
            }

            // Upload new gallery images
            if (selectedImages.length > 0) {
                const promises = selectedImages.map(async (image) => {
                    const storageRef = ref(storage, `news/${image.name}`);
                    const uploadTask = uploadBytesResumable(storageRef, image);
                    await uploadTask;
                    const downloadURL = await getDownloadURL(storageRef);
                    return downloadURL;
                });
                const newGalleryUrls = await Promise.all(promises);
                galleryUrls = [...galleryUrls, ...newGalleryUrls];
            }

            await updateDoc(doc(firestore, "news", id!), {
                thumbnail: thumbnailUrl,
                title: title,
                slug: buildNewsSlug(title, id),
                subtitle: subtitle,
                gallery: galleryUrls,
                description: description,
                deanerName: _selectedDeanery,
                selectedParishName: _selectedParish,
                postedBy: postedBy,
                updatedAt: new Date(),
            });

            showSnackbar("News updated successfully");
            navigate("/admin/news");
        } catch (error) {
            console.error("Error updating news:", error);
            showSnackbar("Error updating news");
        }
    };

    if (loading) {
        return (
            <AdminDashboardLayout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </AdminDashboardLayout>
        );
    }

    return (
        <AdminDashboardLayout>
            <Box flex={1} mt={2} mx={2}>
                <Grid container>
                    <Grid item md={6} lg={6} xl={6} pb={4}>
                        <Card sx={{ width: "100%", borderRadius: 1, m: 3 }} elevation={1}>
                            <CardContent>
                                <Box display="flex" flexDirection="column" pl={1}>
                                    <Box flexDirection="row" display="flex" alignItems="center">
                                        <IconButton onClick={() => navigate("/admin/news")}>
                                            <ArrowBack />
                                        </IconButton>
                                        <Typography variant="h6" fontWeight="600">
                                            Edit News
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Box mt={3}>
                                            <TextField
                                                fullWidth
                                                label="News posted by"
                                                placeholder="Enter name"
                                                value={postedBy}
                                                onChange={(event) => {
                                                    setPostedBy(event?.target.value);
                                                }}
                                                required
                                            />
                                        </Box>

                                        <Box sx={{ mt: 3 }}>
                                            <TextField
                                                fullWidth
                                                label="Title of the news"
                                                placeholder="Enter news title"
                                                value={title}
                                                onChange={(event) => {
                                                    setTitle(event?.target.value);
                                                }}
                                                required
                                            />
                                        </Box>

                                        <Box mt={3}>
                                            <TextField
                                                fullWidth
                                                rows={3}
                                                multiline
                                                label="Subtitle of the news"
                                                placeholder="Enter news subtitle"
                                                value={subtitle}
                                                onChange={(event) => {
                                                    setSubTitle(event?.target.value);
                                                }}
                                                required
                                            />
                                        </Box>

                                        <Box display={"flex"} mt={0} width={"100%"}>
                                            <FormControl fullWidth margin="normal">
                                                <FormLabel>Thumbnail *</FormLabel>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{
                                                        border: `1px solid rgb(181, 176, 176)`,
                                                        width: "96%",
                                                        paddingLeft: 8,
                                                        paddingRight: 8,
                                                        paddingTop: 16,
                                                        paddingBottom: 16,
                                                        borderRadius: 5,
                                                        marginTop: 8,
                                                    }}
                                                    onChange={(e) => {
                                                        if (e.target.files != null) {
                                                            const filesArray = e.target.files[0];
                                                            setSelectedThumbnail(filesArray);
                                                        }
                                                    }}
                                                />
                                                {(selectedThumbnail || existingThumbnail) && (
                                                    <Box mt={2} position="relative" display="inline-block">
                                                        <img
                                                            src={
                                                                selectedThumbnail
                                                                    ? URL.createObjectURL(selectedThumbnail)
                                                                    : existingThumbnail
                                                            }
                                                            alt="Thumbnail Preview"
                                                            style={{
                                                                maxWidth: "200px",
                                                                maxHeight: "150px",
                                                                borderRadius: 5,
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedThumbnail(null);
                                                                if (!selectedThumbnail) {
                                                                    setExistingThumbnail("");
                                                                }
                                                            }}
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                right: 0,
                                                                color: "#ffffff",
                                                                backgroundColor: "rgba(0,0,0,0.5)",
                                                            }}
                                                        >
                                                            <Close />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </FormControl>
                                        </Box>

                                        <Box mt={2}>
                                            <FormControl fullWidth margin="normal">
                                                <FormLabel>Description of the news *</FormLabel>
                                                <ReactQuill
                                                    theme="snow"
                                                    value={description}
                                                    onChange={setDescription}
                                                    placeholder="Enter news description with formatting"
                                                    modules={{
                                                        toolbar: [
                                                            ["bold", "italic", "underline", "strike"],
                                                            ["blockquote", "code-block"],
                                                            [{ header: 1 }, { header: 2 }],
                                                            [{ list: "ordered" }, { list: "bullet" }],
                                                            [{ align: [] }],
                                                            ["link"],
                                                            ["clean"],
                                                        ],
                                                    }}
                                                    formats={[
                                                        "bold",
                                                        "italic",
                                                        "underline",
                                                        "strike",
                                                        "blockquote",
                                                        "code-block",
                                                        "header",
                                                        "list",
                                                        "align",
                                                        "link",
                                                    ]}
                                                    style={{ height: "300px", marginTop: "10px" }}
                                                />
                                            </FormControl>
                                        </Box>

                                        <Box display={"flex"} width={"100%"}>
                                            <FormControl fullWidth margin="normal">
                                                <FormLabel>
                                                    <Typography>Add more images (optional)</Typography>
                                                </FormLabel>
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*"
                                                    style={{
                                                        border: `1px solid rgb(181, 176, 176)`,
                                                        width: "96%",
                                                        paddingLeft: 8,
                                                        paddingRight: 8,
                                                        paddingTop: 16,
                                                        paddingBottom: 16,
                                                        borderRadius: 5,
                                                        marginTop: 8,
                                                    }}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.files != null) {
                                                            const filesArray = Array.from(e.target.files);
                                                            setSelectedImages((prevImages: File[]) => [
                                                                ...prevImages,
                                                                ...filesArray,
                                                            ]);
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                        </Box>

                                        {/* Existing Gallery Images */}
                                        {existingGallery.length > 0 && (
                                            <Box mt={2}>
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    Existing Images
                                                </Typography>
                                                <Box display={"flex"} flexDirection={"row"} flexWrap="wrap">
                                                    {existingGallery.map((imageUrl: string, index: number) => (
                                                        <Box
                                                            key={index}
                                                            display={"flex"}
                                                            flexDirection={"row"}
                                                            position={"relative"}
                                                            mr={1}
                                                            mb={1}
                                                        >
                                                            <img
                                                                src={imageUrl}
                                                                alt="Gallery"
                                                                style={{
                                                                    maxWidth: "100px",
                                                                    maxHeight: "80px",
                                                                    borderRadius: 5,
                                                                }}
                                                            />
                                                            <Box
                                                                display={"flex"}
                                                                position={"absolute"}
                                                                left={0}
                                                                top={0}
                                                            >
                                                                <IconButton
                                                                    size={"small"}
                                                                    onClick={() => removeExistingGalleryImage(imageUrl)}
                                                                    style={{
                                                                        color: "#ffffff",
                                                                        backgroundColor: "rgba(0,0,0,0.5)",
                                                                    }}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* New Images Preview */}
                                        {selectedImages.length > 0 && (
                                            <Box mt={2}>
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    New Images to Upload
                                                </Typography>
                                                <Box display={"flex"} flexDirection={"row"} flexWrap="wrap">
                                                    {selectedImages.map((file: File, index: number) => (
                                                        <Box
                                                            key={index}
                                                            display={"flex"}
                                                            flexDirection={"row"}
                                                            position={"relative"}
                                                            mr={1}
                                                            mb={1}
                                                        >
                                                            <img
                                                                src={URL.createObjectURL(file)}
                                                                alt="Preview"
                                                                style={{
                                                                    maxWidth: "100px",
                                                                    maxHeight: "80px",
                                                                    borderRadius: 5,
                                                                }}
                                                            />
                                                            <Box
                                                                display={"flex"}
                                                                position={"absolute"}
                                                                left={0}
                                                                top={0}
                                                            >
                                                                <IconButton
                                                                    size={"small"}
                                                                    onClick={() => {
                                                                        let filter = selectedImages?.filter(
                                                                            (i: File) => i !== file
                                                                        );
                                                                        setSelectedImages(filter);
                                                                    }}
                                                                    style={{
                                                                        color: "#ffffff",
                                                                        backgroundColor: "rgba(0,0,0,0.5)",
                                                                    }}
                                                                >
                                                                    <Close />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        <Box mt={3}>
                                            <Autocomplete
                                                fullWidth
                                                value={
                                                    deaneryList.find(
                                                        (option) => _selectedDeanery?.id === option.id
                                                    ) || null
                                                }
                                                onChange={handleDeaneryChange}
                                                options={deaneryList}
                                                getOptionLabel={(option) => option.name || ""}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        label="Deanery Name (optional)"
                                                    />
                                                )}
                                            />
                                        </Box>

                                        <Box mt={3}>
                                            <Autocomplete
                                                fullWidth
                                                value={
                                                    parishList.find(
                                                        (option: Parish) =>
                                                            _selectedParish?.id === option.id
                                                    ) || null
                                                }
                                                onChange={(
                                                    e: React.SyntheticEvent,
                                                    value: Parish | null
                                                ) => _setSelectedParish(value)}
                                                options={parishList}
                                                getOptionLabel={(option: Parish) => option.value || ""}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        label="Parish name (optional)"
                                                    />
                                                )}
                                                disabled={!parishList.length}
                                            />
                                        </Box>

                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            justifyContent="space-between"
                                            mt={4}
                                        >
                                            <Button
                                                onClick={() => navigate("/admin/news")}
                                                variant="outlined"
                                                sx={{
                                                    minWidth: 200,
                                                }}
                                            >
                                                Cancel
                                            </Button>

                                            <Button
                                                onClick={handleUpdate}
                                                sx={{
                                                    minWidth: 200,
                                                }}
                                                variant="contained"
                                            >
                                                Update News
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </AdminDashboardLayout>
    );
}

export default EditNewsPage;
